import React from "react";
import { Trail, MantrailingTrail, HikingTrail, isMantrailingTrail, isCanicrossTrail } from "../../types/trail";
import { Card, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Edit, MapPin, Calendar, Trash2 } from "lucide-react";
import TrailIcon from "./TrailIcon";
import HikeIcon from "../icon/HikeIcon";
import CanicrossIcon from "../icon/CanicrossIcon";

interface TrailDetailHeaderProps {
  trail: Trail;
  onEdit: () => void;
  onDelete: () => void;
  isAllowedToEdit: boolean;
}

export function TrailDetailHeader({ trail, onEdit, onDelete, isAllowedToEdit }: TrailDetailHeaderProps) {
  const isMantrailing = isMantrailingTrail(trail);
  const isCanicross = isCanicrossTrail(trail);
  const secondaryTextColor = isCanicross ? "text-orange-100" : "text-blue-100";

  const getTitle = () => {
    if (isMantrailing) {
      const mantrailingTrail = trail as MantrailingTrail;
      return `${typeof mantrailingTrail.dog === "object" ? mantrailingTrail.dog.name : ""} - ${new Date(mantrailingTrail.date).toLocaleDateString('fr-FR')}`;
    } else if (isCanicross) {
      const dog = typeof trail.dogId === "object" ? trail.dogId.name : "Canicross";
      return `${dog} - ${new Date(trail.date).toLocaleDateString('fr-FR')}`;
    } else {
      const hikingTrail = trail as HikingTrail;
      return hikingTrail.name;
    }
  };

  return (
    <Card
      style={isCanicross ? {
        background: "linear-gradient(to right, #ea580c, #c2410c)",
      } : undefined}
      className={`bg-gradient-to-r ${
      isMantrailing
        ? "from-blue-600 to-blue-700"
        : isCanicross
          ? "from-orange-600 to-orange-700"
          : "from-green-600 to-green-700"
    } text-white border-0 shadow-xl`}>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <CardTitle className="break-words text-xl !text-white sm:text-3xl">{getTitle()}</CardTitle>
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30">
                {isMantrailing ? (
                  <>
                    <TrailIcon className="h-3 w-3 mr-1" />
                    Mantrailing
                  </>
                ) : isCanicross ? (
                  <>
                    <CanicrossIcon className="h-3 w-3 mr-1" />
                    Canicross
                  </>
                ) : (
                  <>
                    <HikeIcon className="h-3 w-3 mr-1" />
                    Randonnée
                  </>
                )}
              </Badge>
            </div>
            {typeof trail.location === "string" && trail.location && (
              <div className={`flex items-center gap-2 ${secondaryTextColor}`}>
                <MapPin className="h-4 w-4" />
                <span>{trail.location}</span>
              </div>
            )}
            <div className={`flex items-center gap-2 ${secondaryTextColor}`}>
              <Calendar className="h-4 w-4" />
              <span>{new Date(trail.date).toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
            </div>
          </div>
          {isAllowedToEdit && (
            <div className="flex gap-2 self-start sm:self-auto">
              <Button variant="secondary" size="sm" onClick={onEdit} aria-label="Modifier l'activité" className="gap-2 flex-shrink-0">
                <Edit className="h-4 w-4" />
                <span className="hidden sm:inline">Modifier</span>
              </Button>
              <Button variant="destructive" size="sm" onClick={onDelete} aria-label="Supprimer l'activité" className="gap-2 flex-shrink-0">
                <Trash2 className="h-4 w-4" />
                <span className="hidden sm:inline">Supprimer</span>
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
    </Card>
  );
}